var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Autocomplete, Box, Button, TextField, Typography, } from "@mui/material";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useMergeTeamPlayers, useRetrievePlayersByTeamId, useRetrieveTeam, useRetrieveUserTeamPermissions, } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { playerDisplayName } from "../players/PlayerDisplayNameLink";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
export var MergePlayers = function () {
    var id = useNumericParams().id;
    var navigate = useNavigate();
    var _a = __read(useState(""), 2), errorMessage = _a[0], setErrorMessage = _a[1];
    var _b = useForm(), control = _b.control, handleSubmit = _b.handleSubmit, watch = _b.watch;
    var sourcePlayerId = watch("sourcePlayerId");
    var targetPlayerId = watch("targetPlayerId");
    var _c = useRetrieveUserTeamPermissions({ teamId: id }), permissions = _c.data, permsLoading = _c.isLoading, permsError = _c.error;
    var _d = (permissions || {}).canModify, canModify = _d === void 0 ? false : _d;
    var _e = useRetrieveTeam({ id: id, enabled: canModify }), team = _e.data, teamLoading = _e.isLoading, teamError = _e.error;
    var _f = useRetrievePlayersByTeamId({ teamId: id, enabled: canModify }), players = _f.data, playersLoading = _f.isLoading, playersError = _f.error;
    var mergeMutation = useMergeTeamPlayers({
        teamId: id,
        onSuccess: function () {
            navigate("/teams/".concat(id, "/squad"));
        },
        onError: function () {
            setErrorMessage("Failed to merge players.");
        },
    });
    var onSubmit = function (data) {
        mergeMutation.mutate(data);
    };
    if (permsLoading || teamLoading || playersLoading) {
        return _jsx(Loading, {});
    }
    if (permsError || teamError || playersError) {
        return (_jsx(ErrorView, { error: permsError ||
                teamError ||
                playersError ||
                new Error("Failed to load.") }));
    }
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    var guestPlayers = (players || []).filter(function (p) { return p.isExternal; });
    var registeredPlayers = (players || []).filter(function (p) { return !p.isExternal; });
    if (guestPlayers.length === 0 || registeredPlayers.length === 0) {
        return (_jsxs(React.Fragment, { children: [_jsx(Typography, { variant: "h4", gutterBottom: true, sx: { fontSize: { xs: "1.75rem", sm: "2.125rem" } }, children: "Merge Players" }), _jsx(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 3 }, children: "Merging requires at least one guest player and one registered player. This team does not meet that requirement." }), _jsx(Button, { component: Link, to: "/teams/".concat(id, "/squad"), variant: "contained", children: "Back to Squad" })] }));
    }
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: !!errorMessage, message: errorMessage, onClose: function () { return setErrorMessage(""); } }), _jsx(Typography, { variant: "h4", gutterBottom: true, sx: { fontSize: { xs: "1.75rem", sm: "2.125rem" } }, children: "Merge Players" }), _jsxs(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 3 }, children: ["Merge a guest player into a registered player on ", team === null || team === void 0 ? void 0 : team.name, ". All match data and stats from the guest player will be transferred to the registered player. The guest player will be permanently deleted."] }), _jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), sx: { mt: 1 }, children: [_jsx(Controller, { name: "sourcePlayerId", control: control, render: function (_a) {
                            var _b;
                            var _c = _a.field, value = _c.value, onChange = _c.onChange;
                            return (_jsx(Autocomplete, { options: guestPlayers, getOptionLabel: function (option) { return playerDisplayName(option); }, value: (_b = guestPlayers.find(function (p) { return p.id === value; })) !== null && _b !== void 0 ? _b : null, onChange: function (_, v) { return onChange(v === null || v === void 0 ? void 0 : v.id); }, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { label: "Guest player (will be deleted)", margin: "normal", fullWidth: true }))); }, isOptionEqualToValue: function (option, val) { return option.id === val.id; } }));
                        } }), _jsx(Controller, { name: "targetPlayerId", control: control, render: function (_a) {
                            var _b;
                            var _c = _a.field, value = _c.value, onChange = _c.onChange;
                            return (_jsx(Autocomplete, { options: registeredPlayers, getOptionLabel: function (option) { return playerDisplayName(option); }, value: (_b = registeredPlayers.find(function (p) { return p.id === value; })) !== null && _b !== void 0 ? _b : null, onChange: function (_, v) { return onChange(v === null || v === void 0 ? void 0 : v.id); }, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { label: "Registered player (will receive data)", margin: "normal", fullWidth: true }))); }, isOptionEqualToValue: function (option, val) { return option.id === val.id; } }));
                        } }), _jsxs(Box, { sx: {
                            display: "flex",
                            justifyContent: "center",
                            gap: 2,
                            mt: 3,
                            mb: 2,
                        }, children: [_jsx(Button, { type: "submit", variant: "contained", color: "primary", size: "large", sx: { minWidth: 120 }, disabled: !sourcePlayerId || !targetPlayerId || mergeMutation.isLoading, children: "Merge" }), _jsx(Button, { variant: "outlined", color: "secondary", size: "large", sx: { minWidth: 120 }, onClick: function () { return navigate("/teams/".concat(id, "/squad")); }, children: "Cancel" })] })] })] }));
};
